import React, { useEffect, useRef, useContext } from "react"
import tw from "twin.macro"
import { useTransition, animated } from "react-spring"

import MenuItem, { MenuItemProps } from "./MenuItem"
import { springs } from "../../helpers/constants"

interface IMenuComposition {
  Item: React.FC<MenuItemProps>
}

interface IMenuProps {
  className?: string
  open: boolean
  opener: React.MutableRefObject<any>
  onClose: () => void
}

interface IMenuContext {
  onClose: () => void
}

const MenuContext = React.createContext<IMenuContext | undefined>(undefined)

const Menu: React.FC<IMenuProps> & IMenuComposition = ({
  className,
  children,
  onClose,
  open,
  opener,
  ...htmlProps
}) => {
  if (!opener) {
    throw new Error(
      `Menu component must receive a Ref of the opening component or button`
    )
  }

  const transitions = useTransition(open, null, {
    from: {
      opacity: 0,
      scale: 0.95,
    },
    enter: {
      opacity: 1,
      scale: 1,
    },
    leave: {
      opacity: 0,
      scale: 0.95,
    },
    config: springs.swift,
  })

  const wrapperRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        (wrapperRef.current && wrapperRef.current.contains(event.target)) ||
        opener.current.contains(event.target)
      ) {
        return
      }

      onClose()
    }

    document.addEventListener("mousedown", handleClickOutside)

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [onClose, opener])

  useEffect(() => {
    if (wrapperRef.current) {
      wrapperRef.current.focus()
    }
  }, [wrapperRef])

  const value = { onClose }

  return (
    <>
      {transitions.map(
        ({ item, key, props }) =>
          item && (
            <Wrapper
              key={key}
              style={props}
              ref={wrapperRef}
              className={className}
              {...htmlProps}
            >
              <MenuContext.Provider value={value}>
                {children}
              </MenuContext.Provider>
            </Wrapper>
          )
      )}
    </>
  )
}

export const useMenuContext = () => {
  const context = useContext(MenuContext)
  if (!context) {
    throw new Error(
      `Menu compound components cannot be rendered outside the Menu component`
    )
  }
  return context
}

const Wrapper = tw(animated.ul)`py-1 bg-white rounded-md shadow-xs`

Menu.Item = MenuItem

export default Menu
